import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  SFButton,
  SFAddIcon,
  SFMinusIcon,
  SFGridContainer,
  SFGridContainerItem,
  SFGridItem,
  SFTypography,
  getStyled,
} from "@sf-core";
import { SFStack } from "../grid";

const { styled } = getStyled();

const StyledContainer = styled(SFGridContainerItem)(({ theme }) => ({
  backgroundColor: theme.palette.grey100.main,
  padding: theme.spacing(1),
  margin: theme.spacing(1),
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  padding: theme.spacing(1.25, 1.75),
  backgroundColor: theme.palette.grey100.main,
  borderRadius: 4,
}));

const StyledChildStack = styled(SFStack)(({ theme }) => ({
  padding: theme.spacing(1, 1.75),
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.grey300.main}`,
  borderRadius: 4,
}));

const ParentButton = ({ item, onClickItem, clickable }) => {
  const hasChildren = item.children && item.children.length > 0;
  const [open, setOpen] = useState(false);

  const handleClick = () =>
    hasChildren ? setOpen((prv) => !prv) : onClickItem(item);

  return (
    <>
      {clickable || hasChildren ? (
        <SFGridItem>
          <SFButton
            type="button"
            disabled={item.disabled}
            variant={item.disabled ? "utility" : open ? "primary" : "utility"}
            onClick={handleClick}
          >
            <SFTypography variant="body2bold" sx={{ color: "inherit" }}>
              {item.name}
            </SFTypography>
            {hasChildren && (open ? <SFMinusIcon /> : <SFAddIcon />)}
          </SFButton>
        </SFGridItem>
      ) : (
        <SFGridItem>
          <StyledStack>
            <SFTypography variant="body2bold" color="grey600">
              {item.name}
            </SFTypography>
          </StyledStack>
        </SFGridItem>
      )}
      {open && (
        <StyledContainer spacing={0.5}>
          {item.children.map((btn) => (
            <SFGridItem key={btn.id}>
              {clickable ? (
                <ChildButton
                  item={btn}
                  key={btn.id}
                  onClickItem={onClickItem}
                />
              ) : (
                <StyledChildStack>
                  <SFTypography variant="body2" color="grey600">
                    {btn.name}
                  </SFTypography>
                </StyledChildStack>
              )}
            </SFGridItem>
          ))}
        </StyledContainer>
      )}
    </>
  );
};

const ChildButton = ({ item, onClickItem }) => (
  <SFButton
    type="button"
    disabled={item.disabled}
    variant="secondary"
    onClick={() => onClickItem && onClickItem(item)}
  >
    <SFStack direction="row" spacing={0.5}>
      <SFTypography
        variant="body2"
        color={item.disabled ? "grey400" : "grey500"}
      >
        {item.name}
      </SFTypography>
      {item.subLabel && (
        <SFTypography variant="textLabel">{item.subLabel}</SFTypography>
      )}
    </SFStack>
  </SFButton>
);

const SFButtonList = ({ items, onClickItem, clickable = true }) => (
  <SFGridContainer spacing={1}>
    {items.map((btn) => (
      <ParentButton
        item={btn}
        key={btn.id}
        onClickItem={onClickItem}
        clickable={clickable}
      />
    ))}
  </SFGridContainer>
);

SFButtonList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        })
      ),
    })
  ),
  onClickItem: PropTypes.func,
  clickable: PropTypes.bool,
};

export default SFButtonList;
